import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { getApiEndpoint } from '../config/api'

const AdminDashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('pending')
  const [pendingUsers, setPendingUsers] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [companyInput, setCompanyInput] = useState('')

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/')
      return
    }

    if (activeTab === 'pending') {
      fetchPendingUsers()
    } else {
      fetchAllUsers()
    }
    fetchCompanies()
  }, [user, navigate, activeTab])

  const fetchPendingUsers = async () => {
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(getApiEndpoint('/admin/users/pending'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        setPendingUsers(data.users)
      }
    } catch (error) {
      console.error('Error fetching pending users:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllUsers = async () => {
    const token = localStorage.getItem('token')
    setLoading(true)

    try {
      const response = await fetch(getApiEndpoint('/admin/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        setAllUsers(data.users)
      }
    } catch (error) {
      console.error('Error fetching all users:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCompanies = async () => {
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(getApiEndpoint('/admin/users'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        // Extract unique companies from all users
        const uniqueCompanies = [...new Set(
          data.users
            .map(u => u.company)
            .filter(c => c && c.trim() !== '')
        )].sort()
        setCompanies(uniqueCompanies)
      }
    } catch (error) {
      console.error('Error fetching companies:', error)
    }
  }

  const handleApprove = async (userId) => {
    const token = localStorage.getItem('token')
    setActionLoading(userId)

    try {
      const response = await fetch(`/api/admin/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (data.success) {
        // Remove from pending list
        setPendingUsers(pendingUsers.filter((u) => u._id !== userId))
        alert('User approved successfully!')
      } else {
        alert('Failed to approve user: ' + data.message)
      }
    } catch (error) {
      alert('Error approving user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async (userId) => {
    const reason = prompt('Reason for rejection (optional):')

    const token = localStorage.getItem('token')
    setActionLoading(userId)

    try {
      const response = await fetch(`/api/admin/users/${userId}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rejectionReason: reason }),
      })

      const data = await response.json()

      if (data.success) {
        // Remove from pending list
        setPendingUsers(pendingUsers.filter((u) => u._id !== userId))
        alert('User rejected')
      } else {
        alert('Failed to reject user: ' + data.message)
      }
    } catch (error) {
      alert('Error rejecting user')
    } finally {
      setActionLoading(null)
    }
  }

  const handleUpdateCompany = async (userId, company) => {
    const token = localStorage.getItem('token')
    setActionLoading(userId)

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company }),
      })

      const data = await response.json()

      if (data.success) {
        // Update local state
        setAllUsers(allUsers.map(u =>
          u._id === userId ? { ...u, company } : u
        ))
        setEditingUser(null)
        setCompanyInput('')
        alert('Company updated successfully')

        // Refresh companies list
        fetchCompanies()
      } else {
        alert('Failed to update company: ' + data.message)
      }
    } catch (error) {
      alert('Error updating company')
    } finally {
      setActionLoading(null)
    }
  }

  const startEditingCompany = (user) => {
    setEditingUser(user._id)
    setCompanyInput(user.company || '')
  }

  const cancelEditing = () => {
    setEditingUser(null)
    setCompanyInput('')
  }

  if (!user || user.role !== 'admin') {
    return null
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage user registrations and approvals</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('pending')}
                className={`${
                  activeTab === 'pending'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Pending Approvals ({pendingUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`${
                  activeTab === 'all'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                All Users ({allUsers.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Pending Approvals Tab */}
        {activeTab === 'pending' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                Pending Approvals ({pendingUsers.length})
              </h2>
              <button
                onClick={fetchPendingUsers}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Refresh
              </button>
            </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading...</p>
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No pending approvals</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingUsers.map((pendingUser) => (
                    <tr key={pendingUser._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {pendingUser.firstName} {pendingUser.lastName}
                        </div>
                        {pendingUser.phone && (
                          <div className="text-sm text-gray-500">{pendingUser.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{pendingUser.email}</div>
                        {pendingUser.oauthProvider && (
                          <div className="text-xs text-gray-500">
                            via {pendingUser.oauthProvider === 'google' ? 'Google' : 'Email'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{pendingUser.company}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(pendingUser.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleApprove(pendingUser._id)}
                          disabled={actionLoading === pendingUser._id}
                          className="text-green-600 hover:text-green-900 mr-4 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(pendingUser._id)}
                          disabled={actionLoading === pendingUser._id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        )}

        {/* All Users Tab */}
        {activeTab === 'all' && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                All Users ({allUsers.length})
              </h2>
              <button
                onClick={fetchAllUsers}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading...</p>
              </div>
            ) : allUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allUsers.map((u) => (
                      <tr key={u._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {u.firstName} {u.lastName}
                          </div>
                          {u.phone && (
                            <div className="text-sm text-gray-500">{u.phone}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{u.email}</div>
                          {u.oauthProvider && (
                            <div className="text-xs text-gray-500">
                              via {u.oauthProvider === 'google' ? 'Google' : 'Email'}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingUser === u._id ? (
                            <div className="flex flex-col space-y-2">
                              {/* Dropdown for existing companies */}
                              {companies.length > 0 && (
                                <select
                                  value={companyInput}
                                  onChange={(e) => setCompanyInput(e.target.value)}
                                  className="input-field text-sm"
                                >
                                  <option value="">Select existing company...</option>
                                  {companies.map((comp) => (
                                    <option key={comp} value={comp}>
                                      {comp}
                                    </option>
                                  ))}
                                </select>
                              )}
                              {/* Input for new company */}
                              <input
                                type="text"
                                value={companyInput}
                                onChange={(e) => setCompanyInput(e.target.value)}
                                placeholder="Or enter new company..."
                                className="input-field text-sm"
                              />
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUpdateCompany(u._id, companyInput)}
                                  disabled={!companyInput.trim() || actionLoading === u._id}
                                  className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  disabled={actionLoading === u._id}
                                  className="text-xs bg-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-400 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-900">
                              {u.company || (
                                <span className="text-red-500 italic">Not set</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              u.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : u.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {u.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {u.role}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {editingUser !== u._id && (
                            <button
                              onClick={() => startEditingCompany(u)}
                              disabled={actionLoading === u._id}
                              className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                            >
                              Edit Company
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
